import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import MessageBox from './MessageBox'
import ModalLayout from '../layouts/ModalLayout'
import { fetchWithAuth } from '../utils/fetchWithAuth'
import UserNetworkModal from './UserNetworkModal'
import { modalSecondaryButtonStyle } from '../styles/buttonStyles'
import ExportButton from './ExportButton'
import { exportWalletCSV, exportWalletPDF, exportWalletXLS } from '../utils/walletExport'

const Row = ({ label, value }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '1px 0', lineHeight: 1.45 }}>
    <div>{label}</div>
    <div style={{ fontWeight: 600 }}>{value}</div>
  </div>
)

const WalletModal = ({
  open,
  onClose,
  userId,
  walletBalance = 0,
  walletEntries = [],
  size = 'large'
}) => {
  const { t } = useTranslation()

  const [mode, setMode] = useState('wallet')
  const [pressedBtn, setPressedBtn] = useState(null)
  const [hoverBtn, setHoverBtn] = useState(null)
  const [message] = useState(null)
  const [networkOpen, setNetworkOpen] = useState(false)

  const currency = (n) =>
    new Intl.NumberFormat('sk-SK', {
      style: 'currency',
      currency: 'EUR'
    }).format(Number(n || 0))

  const mapEntryLabel = (e) => {
    const src = String(e.source ?? e.type ?? '').toLowerCase()

    if (src.includes('payout_refund'))
      return t('wallet.payout_refund', { transactionId: e.source_id ?? e.id })

    if (
      src.includes('withdraw') ||
      (src.includes('payout') && !src.includes('refund')) ||
      Number(e.amount) < 0
    )
      return t('wallet.withdraw', { transactionId: e.source_id ?? e.id })

    return t('wallet.income')
  }

  const entryNote = (e) => {
    try {
      const desc = e.description ?? e.note ?? ''
      const parsed = JSON.parse(desc)

      if (parsed?.messageKey) {
        return t(parsed.messageKey, parsed.params || {})
      }
    } catch {}

    let text = e.description ?? e.note ?? ''
    text = text.replace(/\(.*\)$/, '').trim()
    return text || '—'
  }

  const entryDate = (e) => {
    const raw = e.createdAt ?? e.updatedAt ?? e.date ?? e.created_at ?? null
    const d = raw ? new Date(raw) : null
    return d && !isNaN(d.getTime()) ? d.toLocaleString('sk-SK') : ''
  }

  const ymNow = () => {
    const d = new Date()
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
  }

  const getReferralLevel = (e) => {
    try {
      const d = e?.description ? JSON.parse(e.description) : null
      const n = Number(d?.level ?? d?.params?.level)
      if (n === 1 || n === 2 || n === 3) return n
    } catch {}

    const m = String(e?.description ?? '').match(/L\s*([123])/i)
    if (m) {
      const n = Number(m[1])
      if (n === 1 || n === 2 || n === 3) return n
    }

    const n2 = Number(e?.level ?? e?.meta?.level ?? e?.metadata?.level)
    if (n2 === 1 || n2 === 2 || n2 === 3) return n2

    return null
  }

  const referralMonthly = useMemo(() => {

const ymFromDate = (dlike) => {
  const d = dlike ? new Date(dlike) : null
  return d && !isNaN(d.getTime())
    ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
    : ymNow()
}
    const agg = {}

    for (const e of walletEntries) {
      if (String(e.source ?? '').toUpperCase() !== 'REFERRAL_BONUS') continue

      const month =
        e.month || ymFromDate(e.operation_date || e.createdAt || e.updatedAt)

      const lvl = getReferralLevel(e)
      const amt = Number(e.amount ?? 0) || 0

      if (!agg[month]) agg[month] = { l1: 0, l2: 0, l3: 0, total: 0 }

      if (lvl === 1) agg[month].l1 += amt
      else if (lvl === 2) agg[month].l2 += amt
      else if (lvl === 3) agg[month].l3 += amt

      agg[month].total += amt
    }

    return Object.keys(agg)
      .sort()
      .map((m) => ({
        month: m,
        l1: agg[m].l1,
        l2: agg[m].l2,
        l3: agg[m].l3,
        total: agg[m].total
      }))
  }, [walletEntries])

  const monthNow = ymNow()

  const lastReferral = useMemo(() => {
    const row = referralMonthly.find((r) => r.month === monthNow)
    if (!row) return { month: monthNow, l1: 0, l2: 0, l3: 0, total: 0 }
    return row
  }, [referralMonthly, monthNow])

  useEffect(() => {
    if (!open) return
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [open, onClose])

  const { data: cbSummary } = useQuery({
    queryKey: ['wallet-cashback-summary', userId, monthNow],
    enabled: open && !!userId,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const res = await fetchWithAuth(
        `/cashback/${userId}?month=${encodeURIComponent(monthNow)}`
      )
      if (!res.ok) return { base: 0, l1: 0, l2: 0, l3: 0, total: 0 }
      return res.json()
    }
  })

  const cbBase = Number(cbSummary?.base) || 0
  const cbL1 = Number(cbSummary?.l1) || 0
  const cbL2 = Number(cbSummary?.l2) || 0
  const cbL3 = Number(cbSummary?.l3) || 0
  const cbTotal = Number(cbSummary?.total) || 0

  const walletExportRows = walletEntries.map((e) => ({
    id: e.id,
    date: entryDate(e),
    type: mapEntryLabel(e),
    note: entryNote(e),
    amount: Number(e.amount ?? 0) || 0,
    currency: e.currency || 'EUR'
  }))

  if (!open) return null

  const isWallet = mode === 'wallet'

  return (
    <>
      <ModalLayout open={open} onClose={onClose} size={size}>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            padding: '0 16px',
            boxSizing: 'border-box'
          }}
        >
          <div style={{ paddingBottom: 10 }}>
            <h3 style={{ fontSize: 24, margin: 0 }}>{t('stats.wallet')}</h3>
          </div>

          {message && <MessageBox message={message.message} type={message.type} />}

          <div
            style={{
              background: '#fff',
              border: '1px solid #1e3f44',
              borderRadius: 12,
              padding: '8px 16px 10px',
              marginBottom: 12
            }}
          >
            <div style={{ fontWeight: 600, marginBottom: 0, fontSize: 20, lineHeight: 1.1 }}>
              {t('wallet.balance')}
            </div>

            <div style={{ fontSize: 32, fontWeight: 700, lineHeight: 1 }}>
              {currency(walletBalance)}
            </div>

            <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
              <button
                onClick={() => setMode('wallet')}
                style={{
                  flex: 1,
                  height: 40,
                  borderRadius: 8,
                  border: isWallet ? 'none' : '1px solid #1e3f44',
                  background: isWallet ? '#1e3f44' : 'transparent',
                  color: isWallet ? '#fff' : '#1e3f44',
		fontSize: 16
                }}
              >
                {t('stats.wallet_short')}
              </button>

              <button
                onClick={() => setMode('cashback')}
                style={{
                  flex: 1,
                  height: 40,
                  borderRadius: 8,
                  border: !isWallet ? 'none' : '1px solid #1e3f44',
                  background: !isWallet ? '#1e3f44' : 'transparent',
                  color: !isWallet ? '#fff' : '#1e3f44',
		fontSize: 16
                }}
              >
                {t('cashback.label')}
              </button>
            </div>

            <div style={{ marginTop: 12, fontSize: 16 }}>
              {t('wallet.payout_info')}
            </div>
          </div>

{isWallet && (
  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
    
    <div
      style={{
        background: '#fff',
        border: '1px solid #1e3f44',
        borderRadius: 12,
        padding: 16,
        marginBottom: 12,
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginBottom: 10 }}>
        <div style={{ fontWeight: 600 }}>
          {t('wallet.movements')}
        </div>
        <ExportButton
          onCSV={() => exportWalletCSV(walletExportRows, walletBalance, t)}
          onXLS={() => exportWalletXLS(walletExportRows, walletBalance, t)}
          onPDF={() => exportWalletPDF(walletExportRows, walletBalance, t)}
        />
      </div>

      <div
        style={{
        flex: 1,
          overflowY: 'auto',
          minHeight: 0
        }}
      >
        {walletEntries.length === 0 ? (
          <div style={{ opacity: 0.7 }}>
            {t('wallet.noMovements')}
          </div>
        ) : (
          walletEntries.map((e, idx) => {
            const amt = Number(e.amount ?? 0)
            const sign = amt >= 0 ? '+' : '−'
            const color = amt >= 0 ? '#000' : '#B00020'

            return (
              <div
                key={idx}
                style={{
                  padding: '8px 0',
                  borderBottom: '1px solid #ddd'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <div>
                    <div style={{ fontWeight: 600 }}>
                      {mapEntryLabel(e)}
                    </div>
                    <div style={{ fontSize: 14, opacity: 0.7 }}>
                      {entryNote(e)}
                    </div>
                    <div style={{ fontSize: 13, opacity: 0.6 }}>
                      {entryDate(e)}
                    </div>
                  </div>

                  <div style={{ fontWeight: 700, color }}>
                    {sign}
                    {currency(Math.abs(amt))}
                  </div>
                </div>
              </div>
            )
          })
        )}
      </div>
    </div>

  </div>
)}

          {!isWallet && (
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
              <div
                onClick={() => setNetworkOpen(true)}
                style={{

                  background: '#fff',
                  border: '1px solid #1e3f44',
                  borderRadius: 12,
                  padding: 16,
                  overflowY: 'auto',
                  marginBottom: 12,
                  cursor: 'pointer'
                }}
              >
                <div style={{ fontWeight: 600, marginBottom: 0, lineHeight: 1.1 }}>
                  {t('stats.cashback')}: {monthNow}
                </div>

                <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 2 }}>
                  {t('cashback.total')}: {currency(cbTotal)}
                </div>

                <div
                  style={{ borderTop: '1px solid #ddd', marginBottom: 2, marginTop: 2 }}
                />

                <Row label={t('cashback.base')} value={currency(cbBase)} />
                <Row label={t('cashback.level1')} value={currency(cbL1)} />
                <Row label={t('cashback.level2')} value={currency(cbL2)} />
                <Row label={t('cashback.level3')} value={currency(cbL3)} />
              </div>

              <div
                onClick={() => setNetworkOpen(true)}
                style={{

                  background: '#fff',
                  border: '1px solid #1e3f44',
                  borderRadius: 12,
                  padding: 16,
                  overflowY: 'auto',
                  marginBottom: 12,
                  cursor: 'pointer'
                }}
              >
                <div style={{ fontWeight: 600, marginBottom: 0, lineHeight: 1.1 }}>
                  {t('stats.referral')}: {lastReferral.month}
                </div>

                <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 2 }}>
                  {t('cashback.total')}: {currency(lastReferral.total)}
                </div>

                <div
                  style={{ borderTop: '1px solid #ddd', marginBottom: 2, marginTop: 2 }}
                />

                <Row
                  label={t('cashback.level1')}
                  value={currency(lastReferral.l1)}
                />
                <Row
                  label={t('cashback.level2')}
                  value={currency(lastReferral.l2)}
                />
                <Row
                  label={t('cashback.level3')}
                  value={currency(lastReferral.l3)}
                />
              </div>
            </div>
          )}

<div
  style={{
    borderTop: '1px solid #ddd',
    paddingTop: 12,
    paddingBottom: 0,
    display: 'flex',
    justifyContent: 'center'
  }}
>
            <button
              style={modalSecondaryButtonStyle(
                pressedBtn,
                'close',
                hoverBtn === 'close'
              )}
              onMouseEnter={() => setHoverBtn('close')}
              onMouseLeave={() => {
                setHoverBtn(null)
                setPressedBtn(null)
              }}
              onMouseDown={() => setPressedBtn('close')}
              onMouseUp={() => setPressedBtn(null)}
              onClick={onClose}
            >
              {t('CLOSE')}
            </button>
          </div>
        </div>
      </ModalLayout>

      <UserNetworkModal
        open={networkOpen}
        onClose={() => setNetworkOpen(false)}
      />
    </>
  )
}

export default WalletModal